import { CustomSelector, ECustomSelector } from '@globalmed/atlas-ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './NumberOfSeatsTab.module.css';
import { TCreateOrganizationForm } from '../../schema';
import { useGlobalMedDisclaimer } from '../../utils/useGlobalMedDisclaimer';
import { EOrganizationProvisioningScope } from '@/types';

type TProps = {
  disabled?: boolean;
  isScopeLocked?: boolean;
};

/**
 * Number of Seats provisioning tab of the create organization modal: the
 * billing scope selector. The seats and station licenses themselves belong
 * to each contract, see `ContractPeriodFields`.
 * @param props - Component props.
 * @param props.disabled - When true, the scope selector is locked for editing
 * (Org Admins) and shows a disclaimer tooltip on hover.
 * @param props.isScopeLocked - When true, the scope cannot be switched: it is
 * chosen once, when the organization is first saved.
 * @returns JSX.Element representing the number of seats provisioning tab.
 */
export const NumberOfSeatsTab = ({
  disabled = false,
  isScopeLocked = false,
}: TProps) => {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const withDisclaimer = useGlobalMedDisclaimer(disabled);

  return (
    <div className={styles.flexColumn}>
      <Controller
        name="provisioning.scope"
        control={control}
        render={({ field, fieldState }) =>
          withDisclaimer(
            <CustomSelector
              type={ECustomSelector.Select}
              labelProps={{
                label: t('create-organization-modal.billing-customer-label'),
                required: true,
              }}
              placeholder={t(
                'create-organization-modal.billing-customer-placeholder',
              )}
              options={[
                {
                  id: EOrganizationProvisioningScope.ORGANIZATION,
                  title: t(
                    'create-organization-modal.organization-provisioning',
                  ),
                },
                {
                  id: EOrganizationProvisioningScope.INSTITUTION,
                  title: t(
                    'create-organization-modal.institution-provisioning',
                  ),
                },
              ]}
              selected={field.value ?? undefined}
              error={t(fieldState.error?.message ?? '')}
              onChange={field.onChange}
              disabled={isSubmitting || disabled || isScopeLocked}
              sx={{ maxWidth: '600px', width: '100%' }}
            />,
          )
        }
      />
    </div>
  );
};
